import React from "react"
import { Helmet } from "react-helmet-async"
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material"
import SiteWrapper from "../../home-pages/SiteWrapper"

const lineageData = [
  {
    gen: "You",
    name: "Ryan Christopher Dymock",
    born: "1983",
    died: "Living",
    location: "Texas, USA",
    connection: "Son of Paul",
  },
  {
    gen: "1",
    name: "Paul M Dymock",
    born: "1960",
    died: "2025",
    location: "Utah, USA",
    connection: "Son of Garry",
  },
  {
    gen: "2",
    name: "Garry Evan Dymock",
    born: "1934",
    died: "2021",
    location: "Utah, USA",
    connection: "Son of John Ervin",
  },
  {
    gen: "3",
    name: "John Ervin Dymock",
    born: "1912",
    died: "1975",
    location: "Utah, USA",
    connection: "Son of George Edward",
  },
  {
    gen: "4",
    name: "George Edward Dymock",
    born: "1868",
    died: "1932",
    location: "St. John, Tooele, Utah",
    connection:
      "Son of E. George Dymock Sr. & Christine Nelson. Educated at BYU, Provo. 9 children. Died of cancer, obituary in Salt Lake Telegram.",
  },
  {
    gen: "5",
    name: "E. George Dymock Sr. / James Dymoke",
    born: "6 Apr 1828",
    died: "after 1882",
    location: "Norwood, England \u2192 Ohio \u2192 Utah \u2192 Idaho",
    connection:
      'Baptized at St Luke, West Norwood as either George or James (Entry 141 or 143). Called "Mr. E. George Dymock" in the Caldwell family history. Married Caroline Caldwell in Utah, then Sarah Brimhall in Idaho. Sewing machine agent.',
  },
  {
    gen: "6",
    name: "Thomas Dymock",
    born: "1790",
    died: "before 1897",
    location: "Peckham / Lambeth, Surrey",
    connection:
      "Son of Thomas Sr. Married Charlotte Angles 1823. Labourer. Father of 10+ children.",
  },
  {
    gen: "7",
    name: "Thomas Dymock Sr.",
    born: "~1764",
    died: "29 Mar 1837",
    location: "Steeple Claydon, Bucks \u2192 Lambeth",
    connection:
      "Married Ann Stevens 1784. Buried Lambeth age 73. Connection to Steeple Claydon is strong circumstantial.",
    circumstantial: true,
  },
  {
    gen: "8",
    name: "John Dymock",
    born: "~1704",
    died: "?",
    location: "Wing \u2192 Steeple Claydon, Bucks",
    connection:
      "Married Ann Bellam 1748 at Stowe. 8 children at Steeple Claydon. Probable identification with John baptized 1704 at Wing.",
    circumstantial: true,
  },
  {
    gen: "9",
    name: "John Dymock Sr.",
    born: "~1670s",
    died: "?",
    location: "Wing, Buckinghamshire",
    connection:
      "Wife: Ann. Children at Wing 1700\u20131711. Probable \u2014 based on surname rarity and location.",
  },
  {
    gen: "10",
    name: "Christopher Dymock",
    born: "~1667",
    died: "?",
    location: "Wing / East Claydon, Bucks",
    connection:
      "Wife: Elizabeth. Baptized 1667 at Wing. Children at Wing (1681) and East Claydon (1695\u20131698).",
  },
  {
    gen: "11",
    name: "Edward Dymock",
    born: "12 Sep 1658",
    died: "25 Apr 1714",
    location: "Radclive \u2192 Wing \u2192 Thornborough",
    connection:
      "Wife: Sarah. Baptized at Radclive. Children at Wing 1663\u20131667, Thornborough 1686\u20131696. Buried Thornborough (Find a Grave).",
  },
  {
    gen: "12",
    name: "Thomas Dymock",
    born: "?",
    died: "?",
    location: "Radclive, Buckinghamshire",
    connection:
      "Wife: Mary. Named as parents on Edward's 1658 baptism. Earliest confirmed Dymock ancestor.",
    confirmed: true,
  },
  {
    gen: "13?",
    name: "Thomas Dymock of Bletchley",
    born: "?",
    died: "?",
    location: "Bletchley, Buckinghamshire",
    connection:
      "11 children at Bletchley 1603\u20131624 (M2R5-GTL). Had sons Thomas (1607) and Thomas (1610). Hypothetical connection to Thomas of Radclive \u2014 same rare surname, ~12 miles apart.",
    hypothetical: true,
  },
]

const DymockHistory = () => {
  return (
    <SiteWrapper>
      <Helmet>
        <title>Dymock History | The Dymock Line</title>
      </Helmet>

      <Box sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 4 } }}>
          {/* Page Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#1e3a8a", m: 0 }}
            >
              The Dymock Line
            </Typography>
            <Typography sx={{ mt: 1, fontSize: "1.1rem", color: "#475569" }}>
              From Buckinghamshire to Texas
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: "#94a3b8" }}
            >
              A Genealogical Research Report &mdash; Ryan Dymock, March 2026
            </Typography>
          </Box>

          {/* Section: Your Line */}
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
            Your Line: Ryan to Radclive
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary", lineHeight: 1.7 }}>
            Twelve confirmed generations &mdash; and possibly a thirteenth.
            From a software developer in Utah to a farmer in Buckinghamshire
            who baptized his son Edward at a tiny village church in 1658, and
            perhaps further to a man raising a family of eleven at Bletchley
            before the King James Bible was published. Here is the chain, as
            we understand it today.
          </Typography>

          {/* Lineage Table */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2, mb: 3 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", bgcolor: "#f8fafc" }}>
                    Gen
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Born</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Died</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Location</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      bgcolor: "#f8fafc",
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    Connection
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineageData.map((row) => (
                  <TableRow
                    key={row.gen}
                    sx={{
                      "&:hover": { bgcolor: "action.hover" },
                      ...(row.hypothetical && {
                        fontStyle: "italic",
                      }),
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        color: row.hypothetical
                          ? "text.secondary"
                          : "text.primary",
                      }}
                    >
                      {row.gen}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>
                      {row.name}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.born}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.died}
                    </TableCell>
                    <TableCell>{row.location}</TableCell>
                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        color: "text.secondary",
                        fontSize: "0.8125rem",
                        maxWidth: 360,
                      }}
                    >
                      {row.connection}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Narrative */}
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>
            The first five generations (Ryan through Edward George Sr.) follow
            the American branch &mdash; from Utah back through Idaho and the
            frontier West to the port of entry from England. The middle
            generations (Thomas through John) trace the family in south
            London&rsquo;s Lambeth and Norwood. The deepest generations (John
            Sr. through Thomas of Radclive) root the family in a cluster of
            tiny Buckinghamshire villages &mdash; Wing, Steeple Claydon, East
            Claydon, Thornborough, and Radclive &mdash; spanning nearly 200
            years. The hypothetical 13th generation at Bletchley would push the
            line into the early 1600s.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>
            The bolded links between generations 7&ndash;8 and 8&ndash;9 are
            circumstantial but supported by surname rarity, geographic
            clustering, and chronological fit. The generation 13 link (Bletchley
            to Radclive) is unproven. All other links are confirmed by original
            documents.
          </Typography>

          {/* Note */}
          <Box
            sx={{
              borderLeft: "3px solid #1e3a8a",
              bgcolor: "#f8fafc",
              px: 2.5,
              py: 1.5,
              borderRadius: "0 8px 8px 0",
              mt: 3,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              Generations 5&ndash;12 count from Edward George Sr./James
              Dymoke, your direct ancestor who emigrated. His twin brother
              George (LV81-1YR), who stayed in England and married Harriet
              Goble, represents a parallel branch &mdash; the English Dymock
              line that continues to this day.
            </Typography>
          </Box>
        </Box>
      </Box>
    </SiteWrapper>
  )
}

export default DymockHistory
